import React, { useEffect, useMemo, useState } from 'react';
import {
  DollarSign, FileText, TrendingUp, Users, Calendar,
} from 'lucide-react';
import { getApiBaseUrl } from '../config/api';
import StatsCard from './analytics/StatsCard';
import RevenueTrendChart from './analytics/RevenueTrendChart';
import CoverDistributionChart from './analytics/CoverDistributionChart';
import TopPerformingAgentsChart from './analytics/TopPerformingAgentsChart';
import {
  fetchAnalyticsSummary,
  formatCurrency,
  formatNumber,
} from '../services/adminAnalyticsApi';
import '../styles/analytics-theme.css';

const PERIODS = [
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
];

function aggregateDailyToWeekly(dailyData) {
  if (!dailyData?.length) return [];
  const weekly = {};
  dailyData.forEach((item) => {
    const date = new Date(item.date);
    const startOfWeek = new Date(date);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);
    startOfWeek.setHours(0, 0, 0, 0);
    const weekKey = startOfWeek.toISOString().split('T')[0];
    if (!weekly[weekKey]) {
      weekly[weekKey] = { month: weekKey, premium: 0, commission: 0, count: 0 };
    }
    weekly[weekKey].premium += Number(item.revenue) || 0;
    weekly[weekKey].count += Number(item.policies) || 0;
  });
  return Object.values(weekly).sort((a, b) => new Date(a.month) - new Date(b.month));
}

const RevenueAnalytics = ({ onShowNotification }) => {
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [summary, setSummary] = useState(null);
  const [periodTrend, setPeriodTrend] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setFetching(true);
      try {
        const token = localStorage.getItem('adminToken');
        const headers = { Authorization: `Bearer ${token}` };
        const base = getApiBaseUrl();

        const [dashRes, summaryData, revenueRes] = await Promise.all([
          fetch(`${base}/api/admin/dashboard`, { headers }),
          fetchAnalyticsSummary(),
          fetch(`${base}/api/admin/revenue?period=${timeRange}`, { headers }),
        ]);

        if (cancelled) return;

        if (dashRes.ok) {
          const dashJson = await dashRes.json();
          setDashboard(dashJson.data || null);
        }

        setSummary(summaryData);

        if (revenueRes.ok) {
          const revenueJson = await revenueRes.json();
          const daily = revenueJson.data?.dailyRevenue?.map((item) => ({
            date: item.date,
            revenue: parseFloat(item.revenue) || 0,
            policies: parseInt(item.policies, 10) || 0,
          })) || [];
          setPeriodTrend(aggregateDailyToWeekly(daily));
        } else {
          setPeriodTrend([]);
        }
      } catch (error) {
        if (!cancelled && onShowNotification) {
          onShowNotification(error.message || 'Error loading revenue data', 'error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFetching(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [timeRange, onShowNotification]);

  const monthlyTrend = summary?.monthlyTrend || [];
  const coverDistribution = summary?.coverDistribution || [];
  const topAgents = summary?.topAgents || [];

  const periodRevenue = useMemo(
    () => periodTrend.reduce((s, i) => s + (Number(i.premium) || 0), 0),
    [periodTrend]
  );
  const periodPolicies = useMemo(
    () => periodTrend.reduce((s, i) => s + (Number(i.count) || 0), 0),
    [periodTrend]
  );

  const growthRate = useMemo(() => {
    if (periodTrend.length < 2) return null;
    const mid = Math.floor(periodTrend.length / 2);
    const first = periodTrend.slice(0, mid);
    const second = periodTrend.slice(mid);
    const avg = (arr) => arr.reduce((s, i) => s + i.premium, 0) / (arr.length || 1);
    const a = avg(first);
    const b = avg(second);
    if (!a) return null;
    return ((b - a) / a) * 100;
  }, [periodTrend]);

  if (loading) {
    return (
      <div className="ab-page">
        <div className="ab-empty">Loading revenue analytics…</div>
      </div>
    );
  }

  return (
    <div className="ab-page">
      <div className="ab-page-header">
        <div>
          <h1 className="ab-page-title">Revenue Analytics</h1>
          <p className="ab-page-subtitle">Track and analyze your insurance business performance</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          {fetching && <span className="ab-refresh-badge">Refreshing…</span>}
          <div className="ab-segmented" role="group" aria-label="Time range">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`ab-segment ${timeRange === p.id ? 'active' : ''}`}
                onClick={() => setTimeRange(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="ab-stats-grid ab-stats-grid-4">
        <StatsCard
          title="Total Revenue"
          value={formatCurrency(dashboard?.totalRevenue)}
          subtitle="lifetime production (paid premium)"
          icon={DollarSign}
          variant="primary"
        />
        <StatsCard
          title="Monthly Revenue"
          value={formatCurrency(dashboard?.monthlyRevenue)}
          subtitle="this month's production"
          icon={Calendar}
        />
        <StatsCard
          title={`Period Revenue (${timeRange})`}
          value={formatCurrency(periodRevenue)}
          subtitle={growthRate != null ? `${growthRate >= 0 ? '+' : ''}${growthRate.toFixed(1)}% vs prior weeks` : 'selected period'}
          icon={TrendingUp}
        />
        <StatsCard
          title="Total Policies"
          value={formatNumber(dashboard?.totalPolicies || dashboard?.totalTransactions || dashboard?.totalCovers)}
          subtitle={`${formatNumber(periodPolicies)} completed payments in period`}
          icon={FileText}
        />
      </div>

      <div className="ab-stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '1.5rem' }}>
        <StatsCard title="Active Agents" value={formatNumber(dashboard?.totalAgents)} subtitle="registered agents" icon={Users} />
        <StatsCard title="Pending KYC" value={formatNumber(dashboard?.pendingKyc)} subtitle="awaiting verification" icon={Users} />
      </div>

      <div className="ab-charts-grid" style={{ marginBottom: '1.5rem' }}>
        <RevenueTrendChart trendData={monthlyTrend.length ? monthlyTrend : periodTrend} />
        <CoverDistributionChart distribution={coverDistribution} />
      </div>

      <TopPerformingAgentsChart topAgents={topAgents} />
    </div>
  );
};

export default RevenueAnalytics;
